/**
 * Attache un fichier (ex. reçu PDF) aux documents complémentaires d’une fiche People.
 */
import { filesPlatformAPI, peoplePlatformAPI } from '@/services';

function normalizeFileIds(raw) {
  return (Array.isArray(raw) ? raw : [])
    .map((x) => String(x || '').trim())
    .filter(Boolean);
}

/**
 * Upload un File/Blob nommé puis l’ajoute à `document_file_ids` de la personne.
 * Déduplique si un fichier avec le même nom est déjà attaché.
 * @returns {{ fileId: string, alreadyAttached: boolean }}
 */
export async function attachFileToPersonDocuments(personId, file) {
  const pid = String(personId || '').trim();
  if (!pid) throw new Error('missing_person_id');
  if (!file) throw new Error('missing_file');

  const filename = String(file.name || 'document.pdf').trim() || 'document.pdf';

  const personRes = await peoplePlatformAPI.getById(pid);
  const person = personRes?.data || {};
  const existing = normalizeFileIds(person.document_file_ids);

  // Déjà présent sous le même nom d’affichage ?
  for (const fid of existing) {
    try {
      const metaRes = await filesPlatformAPI.get(fid);
      const existingName = String(metaRes?.data?.filename || '').trim();
      if (existingName && existingName === filename) {
        return { fileId: fid, alreadyAttached: true };
      }
    } catch {
      // ignorer métadonnée manquante
    }
  }

  const fileToUpload =
    file instanceof File
      ? file
      : new File([file], filename, { type: file.type || 'application/pdf' });

  const uploaded = await filesPlatformAPI.upload(fileToUpload);
  const fileId = String(uploaded?.data?.id || uploaded?.data?.file_id || '').trim();
  if (!fileId) throw new Error('upload_failed');

  if (existing.includes(fileId)) {
    return { fileId, alreadyAttached: true };
  }

  await peoplePlatformAPI.update(pid, {
    document_file_ids: [...existing, fileId],
  });

  return { fileId, alreadyAttached: false };
}

/** Nom de fichier stable pour un reçu de cotisation (dédup / affichage documents). */
export function buildContributionReceiptDocumentFilename(contributions = []) {
  const rows = Array.isArray(contributions) ? contributions.filter(Boolean) : [];
  const id = String(rows[0]?.id || 'cotisation').trim().slice(0, 12) || 'cotisation';
  const month = String(rows[0]?.month || '')
    .trim()
    .slice(0, 7)
    .replace(/[^\d-]/g, '');
  const suffix = month || 'recu';
  return `recu-cotisation-${id}-${suffix}.pdf`;
}
